import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("pwa-dismissed") === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari has no beforeinstallprompt — show manual instructions.
    if (isIOS()) {
      const t = setTimeout(() => setShow(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (isIOS()) { setIosHint(true); return; }
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("pwa-dismissed", "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed z-[60] left-1/2 -translate-x-1/2 bottom-20 lg:bottom-6 w-[calc(100%-2rem)] max-w-md animate-fade-in-up"
    >
      <div className="bg-navy text-white rounded-xl shadow-xl border border-white/10 p-4">
        <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="absolute top-3 right-3 text-white/50 hover:text-white" aria-label="Fermer">
          <X size={16} />
        </button>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-11 w-11 rounded-lg bg-white p-1 object-contain shrink-0" />
          <div className="pr-4">
            <p className="font-serif text-lg leading-tight">Installer Droits IA</p>
            <p className="text-xs text-white/60">Accès rapide depuis votre écran d'accueil, sans navigateur.</p>
          </div>
        </div>

        {iosHint ? (
          <p className="mt-3 text-sm text-white/80 flex items-center gap-1.5">
            Appuyez sur <Share size={15} className="text-gold" /> puis « Sur l'écran d'accueil ».
          </p>
        ) : (
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-navy font-semibold py-2.5 rounded-md transition-all duration-200"
          >
            <Download size={16} /> Installer l'application
          </button>
        )}
      </div>
    </div>
  );
}
